// Form rules for a project, apart from the component that draws them.
// They mirror data/schemas/project.js and data/models/project/project.js.

export const NAME_MAX = 160;
export const SLUG_MIN = 2;
export const SLUG_MAX = 80;

// The backend's pattern, copied exactly: lowercase, digits, and single
// hyphens between words. Not a looser version of it — a form that accepts
// "Mi Proyecto" only to be refused is worse than one that never took it.
export const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

// The domain state. Separate from `status`, which is the row's lifecycle —
// two columns on purpose (data/models/project/project.js).
export const PROJECT_STATE_OPTIONS = [
    { value: 'active', label: 'Activo',  hint: 'Se está trabajando' },
    { value: 'paused', label: 'Pausado', hint: 'Sigue vivo, pero detenido' },
    { value: 'closed', label: 'Cerrado', hint: 'Terminado, no vuelve' },
];

export const STATUS_OPTIONS = [
    { value: 2, label: 'Activo' },
    { value: 1, label: 'Inactivo' },
];

// Read only. It starts at `discovery`, the API does NOT accept it on create,
// and it only covers the two phases before tiers exist — once they do, the
// phase lives in `tiers.phase`.
export const BOOTSTRAP_PHASE_LABEL = {
    discovery:    'Descubrimiento',
    architecture: 'Arquitectura',
    ready:        'Lista para tiers',
};

export const EMPTY_VALUES = {
    client_id: '',
    name: '',
    slug: '',
    description: '',
    project_state: 'active',
    status: 2,
};

export function valuesFrom(project) {
    return {
        client_id: project?.client_id != null ? String(project.client_id) : '',
        name: project?.name ?? '',
        slug: project?.slug ?? '',
        description: project?.description ?? '',
        project_state: project?.project_state ?? 'active',
        status: project?.status ?? 2,
    };
}

// Name → slug. Strips accents first, because "Diseño" has to become "diseno"
// and not "dise-o": normalizing to NFD splits the tilde off the n, and the
// range strip removes the mark on its own.
export function slugify(name) {
    return name
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, SLUG_MAX);
}

export function validate(values) {
    const errors = {};
    const name = values.name.trim();
    const slug = values.slug.trim();

    if (!values.client_id) errors.client_id = 'El cliente es requerido';

    if (!name) errors.name = 'El nombre del proyecto es requerido';
    else if (name.length > NAME_MAX) errors.name = `No puede pasar de ${NAME_MAX} caracteres`;

    if (!slug) errors.slug = 'El slug es requerido';
    else if (slug.length < SLUG_MIN) errors.slug = `Debe tener al menos ${SLUG_MIN} caracteres`;
    else if (slug.length > SLUG_MAX) errors.slug = `No puede pasar de ${SLUG_MAX} caracteres`;
    else if (!SLUG_PATTERN.test(slug)) {
        errors.slug = 'Solo minúsculas, números y guiones simples';
    }

    return errors;
}

// `bootstrap_phase` is never sent: the API does not accept it on create and
// the panel has no business moving a project's phase from a text field.
export function toPayload(values) {
    return {
        client_id: Number(values.client_id),
        name: values.name.trim(),
        slug: values.slug.trim(),
        description: values.description.trim() || null,
        project_state: values.project_state,
        status: Number(values.status),
    };
}

// Two steps. The first is identity — whose it is and how it will be named in
// every URL from now on; the slug is the field nobody wants to discover buried
// under a description box.
export const CREATE_STEPS = [
    { title: 'Identidad', fields: ['client_id', 'name', 'slug'] },
    { title: 'Estado y detalle', fields: ['description', 'project_state', 'status'] },
];
